if (!customElements.get('product-form')) {
    customElements.define(
        'product-form',
        class ProductForm extends HTMLElement {
            constructor() {
                super();

                this.form = this.querySelector('form');
                this.form.querySelector('[name=id]').disabled = false;
                this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
                this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
                this.submitButton = this.querySelector('[type="submit"]');

                if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

                this.hideErrors = this.dataset.hideErrors === 'true';

                // Load multi-pack cart config if available
                this.multiPackConfig = this.loadMultiPackConfig();

                // Some app blocks (like Stay AI) can render purchase inputs outside
                // the product form when product_form_id is blank. Keep those inputs
                // associated to this form defensively so submit logic is consistent.
                this.ensureExternalFormAssociation();
                setTimeout(() => this.ensureExternalFormAssociation(), 300);
                setTimeout(() => this.ensureExternalFormAssociation(), 1000);
            }

            /**
             * Load the multi-pack cart configuration from the page
             * This config maps multi-pack variants to 1-pack with correct selling plans
             */
            loadMultiPackConfig() {
                const configEl = document.getElementById('multi-pack-cart-config');
                if (!configEl) return null;

                try {
                    return JSON.parse(configEl.textContent);
                } catch (e) {
                    console.error('[MultiPackCart] Failed to parse config:', e);
                    return null;
                }
            }

            /**
             * Check if a variant is a multi-pack and get its configuration
             * @param {string|number} variantId - The variant ID to check
             * @returns {Object|null} - Pack config or null if not a multi-pack
             */
            getMultiPackVariantConfig(variantId) {
                if (!this.multiPackConfig?.variants) return null;
                return this.multiPackConfig.variants[variantId] || null;
            }

            /**
             * Get the correct selling plan ID for a pack size
             * @param {number} packCount - The pack size (1, 2, or 4)
             * @returns {number|null} - The selling plan ID or null
             */
            getSellingPlanForPackSize(packCount) {
                if (!this.multiPackConfig?.sellingPlanMap) return null;
                const planId = this.multiPackConfig.sellingPlanMap[packCount];
                return planId && planId !== 'null' ? planId : null;
            }

            normalizeSellingPlanId(planId) {
                if (planId === null || typeof planId === 'undefined') return null;
                const normalized = String(planId).trim();
                if (!normalized || normalized === 'null' || normalized === 'undefined') return null;
                return normalized;
            }

            ensureExternalFormAssociation(productInfoSection = this.getProductInfoSection()) {
                if (!this.form || !this.form.id) return;

                const scope = productInfoSection || document;
                const selectors = ['[name="selling_plan"]', '#purchaseTypeSubscription', '#purchaseTypeOneTime'];
                selectors.forEach((selector) => {
                    scope.querySelectorAll(selector).forEach((input) => {
                        if (!input || input.form === this.form) return;
                        if (!input.getAttribute('form')) {
                            input.setAttribute('form', this.form.id);
                        }
                    });
                });
            }

            // Ensure a hidden input named `name` exists in this.form and set its value.
            // Marked with data-multipack="true" so the helper can clean them up later
            // without touching unrelated hidden inputs the theme may render.
            setOrCreateHiddenInput(name, value) {
                if (!this.form) return null;
                let input = this.form.querySelector(`input[type="hidden"][name="${name}"][data-multipack="true"]`);
                if (!input) {
                    input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = name;
                    input.dataset.multipack = 'true';
                    this.form.appendChild(input);
                }
                input.value = String(value);
                return input;
            }

            removeMultiPackHiddenInputs() {
                if (!this.form) return;
                this.form.querySelectorAll('input[data-multipack="true"]').forEach((input) => input.remove());
            }

            // Apply quantity + bundle property hidden inputs to the form so the existing
            // FormData submit path (the one 1-pack uses) sends the right line item.
            // For 1-pack we strip prior multi-pack inputs so a previous selection on the
            // same page life doesn't leak into the next add.
            applyMultiPackFormFields(packCount) {
                this.removeMultiPackHiddenInputs();

                const safeCount = parseInt(packCount, 10) || 1;
                if (safeCount <= 1) return;

                const bundleLabel = `${safeCount} Pack`;
                this.setOrCreateHiddenInput('quantity', safeCount);
                this.setOrCreateHiddenInput('properties[_bundle_type]', bundleLabel);
                this.setOrCreateHiddenInput('properties[Bundle]', bundleLabel);
            }

            getVariantQuantityInCart(cartData, variantId) {
                if (!cartData || !Array.isArray(cartData.items)) return 0;
                return cartData.items
                    .filter((item) => String(item.variant_id) === String(variantId))
                    .reduce((sum, item) => sum + item.quantity, 0);
            }

            async verifyMultiPackAdded(onePackVariantId, quantity, responseData = null) {
                try {
                    const cartCheckResponse = await fetch('/cart.js', {
                        headers: { 'Content-Type': 'application/json' }
                    });
                    if (!cartCheckResponse.ok) {
                        return { itemInCart: false, cartData: null, totalQuantityInCart: 0 };
                    }

                    const cartData = await cartCheckResponse.json();
                    const totalQuantityInCart = this.getVariantQuantityInCart(cartData, onePackVariantId);
                    const itemInCart = totalQuantityInCart >= quantity;

                    console.log(
                        `[MultiPackCart] Total quantity of variant ${onePackVariantId} in cart: ${totalQuantityInCart}, expected: ${quantity}`
                    );

                    return { itemInCart, cartData, totalQuantityInCart };
                } catch (checkError) {
                    console.error('[MultiPackCart] Could not verify cart state:', checkError);
                    if (responseData && Array.isArray(responseData.items)) {
                        const totalQuantityInCart = this.getVariantQuantityInCart(responseData, onePackVariantId);
                        return {
                            itemInCart: totalQuantityInCart >= quantity,
                            cartData: responseData,
                            totalQuantityInCart,
                        };
                    }
                    return { itemInCart: false, cartData: null, totalQuantityInCart: 0 };
                }
            }

            async recoverOrHandleMultiPackError({ onePackVariantId, quantity, responseData, fallbackMessage }) {
                const { itemInCart, cartData } = await this.verifyMultiPackAdded(onePackVariantId, quantity, responseData);

                if (itemInCart) {
                    console.log('[MultiPackCart] Item detected in cart during recovery, opening drawer');
                    this.error = false;
                    publish(PUB_SUB_EVENTS.cartUpdate, {
                        source: 'product-form',
                        productVariantId: onePackVariantId,
                        cartData: cartData,
                    });

                    if (this.cart) {
                        await this.refreshCartDrawerAndOpen();
                    } else {
                        window.location = window.routes.cart_url;
                    }
                    return true;
                }

                if (responseData && responseData.status) {
                    publish(PUB_SUB_EVENTS.cartError, {
                        source: 'product-form',
                        productVariantId: onePackVariantId,
                        errors: responseData.errors || responseData.description,
                        message: responseData.message,
                    });
                    this.handleErrorMessage(responseData.description || responseData.message || fallbackMessage);
                } else {
                    this.handleErrorMessage(fallbackMessage);
                }

                this.error = true;
                return false;
            }

            /**
             * Add multi-pack items to cart (as multiple 1-pack items)
             * @param {number} onePackVariantId - The 1-pack variant ID
             * @param {number} quantity - Number of 1-pack items to add
             * @param {number|null} sellingPlanId - The selling plan ID for subscriptions
             * @param {string} bundleType - Bundle type label (e.g., "2 Pack")
             */
            async addMultiPackToCart(onePackVariantId, quantity, sellingPlanId, bundleType) {
                const item = {
                    id: onePackVariantId,
                    quantity: quantity,
                    properties: {
                        '_bundle_type': bundleType,
                        'Bundle': bundleType  // Visible property shown in cart
                    }
                };

                // Add selling plan if subscription is selected
                const normalizedSellingPlanId = this.normalizeSellingPlanId(sellingPlanId);
                if (normalizedSellingPlanId) {
                    item.selling_plan = normalizedSellingPlanId;
                }

                // Build the request body
                const body = {
                    items: [item]
                };

                // Add sections for cart drawer update
                if (this.cart) {
                    body.sections = this.cart.getSectionsToRender().map((section) => section.id);
                    body.sections_url = window.location.pathname;
                    this.cart.setActiveElement(document.activeElement);
                }

                try {
                    console.log('[MultiPackCart] Sending cart request:', JSON.stringify(body, null, 2));

                    const response = await fetch(`${routes.cart_add_url}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        },
                        body: JSON.stringify(body)
                    });

                    // Check if response is JSON
                    const contentType = response.headers.get('content-type');
                    let data = null;
                    let parseError = false;

                    if (!response.ok) {
                        parseError = true;
                    }

                    if (!contentType || !contentType.includes('application/json')) {
                        console.warn('[MultiPackCart] Server returned non-JSON response:', response.status);
                        const responseText = await response.text();
                        console.warn('[MultiPackCart] Response body:', responseText.substring(0, 500));
                        parseError = true;
                    } else {
                        try {
                            data = await response.json();
                        } catch (jsonErr) {
                            console.warn('[MultiPackCart] Failed to parse JSON response:', jsonErr);
                            parseError = true;
                        }
                    }

                    // If we got a parse error, HTML response, or cart API error, verify
                    // cart state before surfacing an error so successful adds still open.
                    if (parseError || (data && data.status)) {
                        console.log('[MultiPackCart] Response error or parse issue, verifying cart state...');
                        const recovered = await this.recoverOrHandleMultiPackError({
                            onePackVariantId,
                            quantity,
                            responseData: data,
                            fallbackMessage: 'Failed to add to cart. Please try again.',
                        });
                        if (recovered) return;
                        return;
                    }

                    if (!this.cart) {
                        window.location = window.routes.cart_url;
                        return;
                    }

                    // Success
                    console.log('[MultiPackCart] Successfully added to cart:', data);
                    publish(PUB_SUB_EVENTS.cartUpdate, {
                        source: 'product-form',
                        productVariantId: onePackVariantId,
                        cartData: data,
                    });
                    this.error = false;

                    const quickAddModal = this.closest('quick-add-modal');
                    if (quickAddModal) {
                        document.body.addEventListener(
                            'modalClosed',
                            () => {
                                setTimeout(() => {
                                    this.cart.renderContents(data);
                                });
                            },
                            { once: true }
                        );
                        quickAddModal.hide(true);
                    } else {
                        this.cart.renderContents(data);
                    }
                } catch (e) {
                    console.error('[MultiPackCart] Error adding to cart:', e);
                    await this.recoverOrHandleMultiPackError({
                        onePackVariantId,
                        quantity,
                        responseData: null,
                        fallbackMessage: 'Failed to add to cart. Please try again.',
                    });
                } finally {
                    this.submitButton.classList.remove('loading');
                    if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                    if (!this.error) this.submitButton.removeAttribute('aria-disabled');
                    this.querySelector('.loading__spinner').classList.add('hidden');
                }
            }

            /**
             * Manually refresh cart drawer sections and open the drawer
             * Used as fallback when section rendering fails in the cart add response
             */
            async refreshCartDrawerAndOpen() {
                if (!this.cart) return;

                try {
                    // Get section IDs to refresh
                    const sections = this.cart.getSectionsToRender().map((section) => section.id);

                    // Fetch fresh section content
                    const sectionUrl = `${window.location.pathname}?sections=${sections.join(',')}`;
                    const response = await fetch(sectionUrl);

                    if (!response.ok) {
                        console.warn('[MultiPackCart] Could not fetch sections, opening cart drawer anyway');
                        // Try to open cart drawer with page refresh as fallback
                        if (typeof this.cart.open === 'function') {
                            this.cart.open();
                        }
                        return;
                    }

                    const sectionsData = await response.json();

                    // Create a response object similar to what cart add returns
                    const cartResponse = {
                        sections: sectionsData
                    };

                    console.log('[MultiPackCart] Rendering cart drawer with fresh sections');
                    this.cart.renderContents(cartResponse);
                } catch (e) {
                    console.error('[MultiPackCart] Error refreshing cart drawer:', e);
                    // As a last resort, just try to open the cart drawer
                    if (typeof this.cart.open === 'function') {
                        this.cart.open();
                    }
                }
            }

            getProductInfoSection() {
                if (!this.dataset.sectionId) return null;
                return document.getElementById('ProductInfo-' + this.dataset.sectionId);
            }

            getSellingPlanInputs(productInfoSection = this.getProductInfoSection()) {
                const inputs = [];
                const seen = new Set();

                const inputGroups = [
                    this.form ? Array.from(this.form.querySelectorAll('[name="selling_plan"]')) : [],
                    Array.from(this.querySelectorAll('[name="selling_plan"]')),
                    productInfoSection ? Array.from(productInfoSection.querySelectorAll('[name="selling_plan"]')) : [],
                ];

                inputGroups.flat().forEach((input) => {
                    if (!input || seen.has(input)) return;
                    seen.add(input);
                    inputs.push(input);
                });

                return inputs;
            }

            getFirstNonEmptySellingPlanInput(inputs) {
                return (
                    inputs.find((input) => {
                        if (!input || typeof input.value === 'undefined') return false;
                        return String(input.value).trim() !== '';
                    }) || null
                );
            }

            isSubscriptionSelected(productInfoSection = this.getProductInfoSection()) {
                const scope = productInfoSection || document;
                const subscriptionRadio = scope.querySelector('#purchaseTypeSubscription');
                const oneTimeRadio = scope.querySelector('#purchaseTypeOneTime');

                if (!subscriptionRadio) return false;
                if (subscriptionRadio.checked) return true;
                if (oneTimeRadio) return !oneTimeRadio.checked;

                return false;
            }

            onSubmitHandler(evt) {
                evt.preventDefault();
                if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
                if (!this.form) return;

                this.handleErrorMessage();

                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButton.classList.add('loading');
                this.querySelector('.loading__spinner').classList.remove('hidden');

                const productInfoSection = this.getProductInfoSection();
                this.ensureExternalFormAssociation(productInfoSection);

                // Pack-size selector (custom UI) drives multi-pack behavior now.
                // The 1-Pack is the only real variant; 2/4-Pack are expressed as
                // selling-plan + quantity multiplier at cart-add time.
                const packSelectorActive = typeof window._currentPackCount !== 'undefined' && this.multiPackConfig?.onePackVariantId;

                if (packSelectorActive) {
                    // Read the live checked radio at submit time — guards against the
                    // back-navigation race where form-state restoration sets the radio
                    // after DOMContentLoaded so window._currentPackCount is stale.
                    const packSizeRadio = document.querySelector('pack-size-selector input[name="pack_size"]:checked');
                    const packCount = packSizeRadio
                        ? (parseInt(packSizeRadio.value, 10) || 1)
                        : (parseInt(window._currentPackCount, 10) || 1);
                    window._currentPackCount = packCount;
                    const onePackId = this.multiPackConfig.onePackVariantId;
                    const sellingPlanInputs = this.getSellingPlanInputs(productInfoSection);
                    const sellingPlanInput = this.getFirstNonEmptySellingPlanInput(sellingPlanInputs);
                    const hasSubscription = this.isSubscriptionSelected(productInfoSection);

                    let sellingPlanId = null;
                    if (hasSubscription) {
                        const mappedSellingPlan = this.normalizeSellingPlanId(this.getSellingPlanForPackSize(packCount));
                        const domSellingPlan = this.normalizeSellingPlanId(sellingPlanInput ? sellingPlanInput.value : null);
                        sellingPlanId = mappedSellingPlan || domSellingPlan;

                        // For multi-pack subscription, mapped plans are authoritative.
                        // Fall back to DOM only if mapping is unavailable.
                        if (packCount > 1 && mappedSellingPlan) {
                            sellingPlanId = mappedSellingPlan;
                        }
                    }

                    console.log('[MultiPackCart] Selling plan resolution:', {
                        candidateCount: sellingPlanInputs.length,
                        hasSubscription: hasSubscription,
                        packCount: packCount,
                        resolvedSellingPlanId: sellingPlanId || null,
                        selectedInputValue: sellingPlanInput ? sellingPlanInput.value : null,
                    });

                    // Make 2/4-pack ride the same FormData submit pipeline as 1-pack by
                    // injecting quantity + bundle property hidden inputs into the form.
                    // For 1-pack this also strips any leftover multi-pack inputs from a
                    // previous click in the same page life.
                    this.applyMultiPackFormFields(packCount);

                    // The variant id input is rendered for the storefront's currently
                    // selected variant. With pack-size selectors only the 1-Pack variant
                    // exists, so this is already the right id, but set it explicitly to
                    // protect against themes that swap variant ids on selection.
                    const variantIdInput = this.form.querySelector('input[name="id"]');
                    if (variantIdInput && onePackId) {
                        variantIdInput.value = String(onePackId);
                    }

                    // Ensure the form's selling_plan input reflects the resolved plan
                    // for both 1-pack and multi-pack cases. Stay AI sometimes defaults
                    // to a different plan for the variant; we override here.
                    if (hasSubscription && sellingPlanId && sellingPlanInput && sellingPlanInput.value !== String(sellingPlanId)) {
                        sellingPlanInput.value = String(sellingPlanId);
                    }
                }

                // Normal flow for 1-pack or products without multi-pack config
                const config = fetchConfig('javascript');
                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];

                const formData = new FormData(this.form);

                // FormData(this.form) only collects inputs nested inside <form>.
                // When Stay AI injects [name="selling_plan"] outside <form> (e.g. its
                // block has an empty product_form_id), the plan is missing from the
                // request and the line item is added as a one-time purchase.
                // Re-attach the resolved plan whenever we can find one in the DOM —
                // scoped to #ProductInfo-<sectionId> so cart-drawer upsell product
                // cards (rendered after the first add) don't shadow Stay AI's input.
                const existingPlan = formData.get('selling_plan');
                const sellingPlanInputsForFormData = this.getSellingPlanInputs(productInfoSection);
                const externalSellingPlanInput = this.getFirstNonEmptySellingPlanInput(sellingPlanInputsForFormData);
                const subscriptionModeForFormData = this.isSubscriptionSelected(productInfoSection);

                if ((!existingPlan || existingPlan === '') && subscriptionModeForFormData) {
                    const packCountForFormData = parseInt(window._currentPackCount, 10) || 1;
                    const mappedSellingPlan = this.normalizeSellingPlanId(this.getSellingPlanForPackSize(packCountForFormData));
                    const resolvedPlanForFormData =
                        mappedSellingPlan || this.normalizeSellingPlanId(externalSellingPlanInput && externalSellingPlanInput.value);

                    if (resolvedPlanForFormData) {
                        formData.set('selling_plan', String(resolvedPlanForFormData));
                    }
                }

                console.log('[MultiPackCart] FormData selling plan state:', {
                    existingPlan: existingPlan || null,
                    subscriptionMode: subscriptionModeForFormData,
                    candidateCount: sellingPlanInputsForFormData.length,
                    finalSellingPlan: formData.get('selling_plan') || null,
                });

                if (this.cart) {
                    formData.append(
                        'sections',
                        this.cart.getSectionsToRender().map((section) => section.id)
                    );
                    formData.append('sections_url', window.location.pathname);
                    this.cart.setActiveElement(document.activeElement);
                }
                config.body = formData;

                fetch(`${routes.cart_add_url}`, config)
                    // fetch(window.Shopify.routes.root + 'cart/add.js', config)
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.status) {
                            publish(PUB_SUB_EVENTS.cartError, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                errors: response.errors || response.description,
                                message: response.message,
                            });
                            this.handleErrorMessage(response.description);

                            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                            if (!soldOutMessage) return;
                            this.submitButton.setAttribute('aria-disabled', true);
                            this.submitButton.querySelector('span').classList.add('hidden');
                            soldOutMessage.classList.remove('hidden');
                            this.error = true;
                            return;
                        } else if (!this.cart) {
                            window.location = window.routes.cart_url;
                            return;
                        }

                        if (!this.error)
                            publish(PUB_SUB_EVENTS.cartUpdate, {
                                source: 'product-form',
                                productVariantId: formData.get('id'),
                                cartData: response,
                            });
                        this.error = false;
                        const quickAddModal = this.closest('quick-add-modal');
                        if (quickAddModal) {
                            document.body.addEventListener(
                                'modalClosed',
                                () => {
                                    setTimeout(() => {
                                        this.cart.renderContents(response);
                                    });
                                },
                                { once: true }
                            );
                            quickAddModal.hide(true);
                        } else {
                            this.cart.renderContents(response);
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                    })
                    .finally(() => {
                        this.submitButton.classList.remove('loading');
                        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
                        this.querySelector('.loading__spinner').classList.add('hidden');
                    });
            }

            handleErrorMessage(errorMessage = false) {
                if (this.hideErrors) return;

                this.errorMessageWrapper =
                    this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
                if (!this.errorMessageWrapper) return;
                this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

                this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

                if (errorMessage) {
                    this.errorMessage.textContent = errorMessage;
                }
            }
        }
    );
}
